import type { PixelSettings } from '../config/types/pixel-settings';
import { createPixel } from '../pixel/createPixel';
import type { Pixel } from '../pixel/Pixel';
import { PixelBootstrapError } from './errors';
import type { BootstrapPixelControls } from './types';

const WINDOW_STORE_KEY = '__beworke_pixel_bootstrap__' as const;

type BootstrapStore = {
  instance: Pixel | null;
  pending: Promise<Pixel> | null;
  generation: number;
  gate: Promise<void>;
};

type WindowWithBootstrapStore = Window & {
  [WINDOW_STORE_KEY]?: BootstrapStore;
};

function assertBrowser(): void {
  if (typeof window === 'undefined') {
    throw new PixelBootstrapError(
      'bootstrapPixel só pode ser usado no browser (window ausente).',
      'NOT_BROWSER'
    );
  }
}

function createEmptyStore(): BootstrapStore {
  return {
    instance: null,
    pending: null,
    generation: 0,
    gate: Promise.resolve(),
  };
}

function getStore(): BootstrapStore {
  assertBrowser();
  const win = window as WindowWithBootstrapStore;
  if (!win[WINDOW_STORE_KEY]) {
    win[WINDOW_STORE_KEY] = createEmptyStore();
  }
  return win[WINDOW_STORE_KEY]!;
}

function peekStore(): BootstrapStore | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (window as WindowWithBootstrapStore)[WINDOW_STORE_KEY];
}

function isReusableInstance(instance: Pixel): boolean {
  return instance.isInitialized();
}

function wrapInitError(error: unknown): never {
  if (error instanceof PixelBootstrapError) {
    throw error;
  }
  throw new PixelBootstrapError(
    error instanceof Error ? error.message : 'Falha ao inicializar o Pixel.',
    'INIT_FAILED',
    error
  );
}

/**
 * Uma instância por `window`. Sem `force`, a primeira chamada é dona da
 * configuração; chamadas posteriores reutilizam pending/instância.
 *
 * Não é `async`: o retorno é a Promise do store para chamadas concorrentes
 * compartilharem a mesma referência.
 *
 * Para trocar a configuração, use `force: true` ou `destroyBootstrappedPixel()`.
 * Chamadas concorrentes compartilham a configuração da primeira que iniciou.
 */
export function bootstrapPixel(
  settings: PixelSettings,
  controls?: BootstrapPixelControls
): Promise<Pixel> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new PixelBootstrapError(
        'bootstrapPixel só pode ser usado no browser (window ausente).',
        'NOT_BROWSER'
      )
    );
  }

  const store = getStore();
  const force = controls?.force === true;
  const waitForConsent = controls?.waitForConsent;

  if (!force) {
    if (store.pending) {
      return store.pending;
    }
    if (store.instance && isReusableInstance(store.instance)) {
      return Promise.resolve(store.instance);
    }
  }

  const generation = ++store.generation;
  const previousGate = store.gate;
  let gateReleased = false;
  let releaseGate = (): void => undefined;
  store.gate = new Promise<void>(resolve => {
    releaseGate = resolve;
  });

  const releaseGateOnce = (): void => {
    if (!gateReleased) {
      gateReleased = true;
      releaseGate();
    }
  };

  const pending: Promise<Pixel> = (async () => {
    try {
      if (force) {
        try {
          await previousGate;
        } catch {
          // bootstrap anterior falhou; segue
        }

        const currentInstance = store.instance;
        if (currentInstance) {
          try {
            await currentInstance.destroy();
          } catch {
            // segue recriando
          } finally {
            if (store.instance === currentInstance) {
              store.instance = null;
            }
          }
        }
      }

      const joinLatest = (): Promise<Pixel> | Pixel | null => {
        if (store.generation !== generation) {
          if (store.pending && store.pending !== pending) {
            return store.pending;
          }
          if (store.instance && isReusableInstance(store.instance)) {
            return store.instance;
          }
        }
        return null;
      };

      const superseded = joinLatest();
      if (superseded) {
        releaseGateOnce();
        return superseded;
      }

      if (waitForConsent) {
        const allowed = await waitForConsent();
        if (!allowed) {
          throw new PixelBootstrapError(
            'Consentimento negado — bootstrap abortado.',
            'CONSENT_DENIED'
          );
        }
      }

      const supersededAfterConsent = joinLatest();
      if (supersededAfterConsent) {
        releaseGateOnce();
        return supersededAfterConsent;
      }

      try {
        const instance = await createPixel(settings);
        if (store.generation !== generation) {
          const latest = joinLatest();
          if (latest) {
            releaseGateOnce();
            try {
              await instance.destroy();
            } catch {
              // instância obsoleta
            }
            return latest;
          }
        }
        return instance;
      } catch (error) {
        wrapInitError(error);
      }
    } finally {
      releaseGateOnce();
    }
  })()
    .then(instance => {
      if (store.generation === generation) {
        store.instance = instance;
        if (store.pending === pending) {
          store.pending = null;
        }
      } else if (store.instance == null) {
        store.instance = instance;
      } else if (store.instance !== instance) {
        void instance.destroy().catch(() => {
          // ignore
        });
      }
      return instance;
    })
    .catch((error: unknown) => {
      if (store.pending === pending && store.generation === generation) {
        store.pending = null;
      }
      throw error;
    });

  store.pending = pending;
  return pending;
}

export function getBootstrappedPixel(): Pixel | null {
  return peekStore()?.instance ?? null;
}

export function getBootstrappedPixelPending(): Promise<Pixel> | null {
  return peekStore()?.pending ?? null;
}

export async function destroyBootstrappedPixel(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const store = peekStore();
  if (!store) {
    return;
  }

  store.generation += 1;

  if (store.pending) {
    try {
      await store.pending;
    } catch {
      // ignore
    }
    store.pending = null;
  }

  if (store.instance) {
    await store.instance.destroy();
    store.instance = null;
  }
}

/** @internal limpa store sem destroy (testes). Preferir destroyBootstrappedPixel. */
export function clearBootstrapStoreForTests(): void {
  if (typeof window === 'undefined') {
    return;
  }
  delete (window as WindowWithBootstrapStore)[WINDOW_STORE_KEY];
}
